import {
  DeleteOutlined,
  DownOutlined,
  FolderAddOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Popover, Row, Tooltip } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DeleteTemplateApi from "../../api/plan-templates/DeleteTemplateApi";
import { GetPlanTemplatesApi } from "../../api/plan-templates/GetPlanTemplatesApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import AddPlansTemplateModal from "../../components/modal/add/AddPlansTemplateModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import PageLoader from "../../components/sub/PageLoader";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import { permissionAbility } from "../../helpers/permissionHelpers";

function PlanTemplates(props) {
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState("");
  const [selected, setSelected] = useState();
  const [templates, setTemplates] = useState([]);
  const setupAccountState = useSelector(
    (state) => state.setupAccountState.value
  );
  const proHeight = document.body.clientHeight;
  let navigate = useNavigate();
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    getPlanTemplates();
  }, []);

  async function getPlanTemplates() {
    setLoading(true);
    const apiResponse = await GetPlanTemplatesApi();
    if (apiResponse.success) {
      setTemplates(apiResponse.data);
      setLoading(false);
    }
  }

  async function deleteTemplate() {
    setLoading(true);
    const apiResponse = await DeleteTemplateApi(selected?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
      setLoading(false);
    } else if (apiResponse.success) {
      openNotificationSuccess(
        "bottomRight",
        `Template ${deleteModal} successfully`
      );
      getPlanTemplates();
    } else {
      openNotificationError(
        "bottomRight",
        `"Couldn't ${deleteModal} template. Try again later."`
      );
      setLoading(false);
    }
  }

  const right = (
    <>
      {permissionAbility(userLevel, "plan-templates", "add") &&
        templates.length < 10 && (
          <Tooltip placement="left" title={"Add Plan Templates"}>
            <Button
              onClick={() => setAddModal(true)}
              shape="circle"
              className="ml-3"
              type="primary"
              icon={<PlusOutlined />}
            ></Button>
          </Tooltip>
        )}
    </>
  );

  const left = <div className="text-lg pri-font">Plan Templates</div>;

  function getLevels(temp) {
    let outputLevels = [];

    temp?.planLevels?.sort((a, b) => {
      return a.order - b.order;
    });

    temp?.planLevels?.forEach((level) => {
      outputLevels.push(
        <div key={temp?.planLevels?.id}>
          <div className="flex text-black border p-3 rounded">
            <div className="mr-2 flex items-center">{level.order}</div>
            <div
              style={{ borderColor: "#d9d9d9" }}
              className="border p-1 rounded mr-2"
            >
              <div
                style={{ backgroundColor: level.color || "#ccc" }}
                className="w-6 h-full"
              ></div>
            </div>
            <div className="py-1">{level.name}</div>
          </div>
          {temp.planLevels.length != level.order && (
            <div className="text-gray-300 text-center">
              <DownOutlined />
            </div>
          )}
        </div>
      );
    });
    return outputLevels;
  }

  function getTemplates() {
    let output = [];
    templates?.forEach((temp) => {
      output.push(
        <Col span={8} className="mb-4" key={temp.id}>
          <Card
            className="hover:shadow-lg cursor-pointer"
            style={{
              borderColor: "#e5e7eb",
            }}
            onClick={() => {
              navigate(
                setupAccountState
                  ? `/settings/general/plan-templates/${temp.id}`
                  : `/setup-account/general/plan-templates/${temp.id}`
              );
            }}
          >
            <div className="flex justify-between items-center">
              <div className="mb-2 text-gray-400">{temp.name}</div>
              <div className="flex">
                {permissionAbility(userLevel, "plan-templates", "delete") &&
                  (temp.canDelete ? (
                    <Tooltip title={"Delete"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteModal("delete");
                          setSelected(temp);
                        }}
                        className="text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <DeleteOutlined />
                      </div>
                    </Tooltip>
                  ) : temp.canArchive ? (
                    <Tooltip title={"Archive"}>
                      <div
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteModal("archive");
                          setSelected(temp);
                        }}
                        className="text-red-500 hover:text-red-600 cursor-pointer"
                      >
                        <FolderAddOutlined />
                      </div>
                    </Tooltip>
                  ) : (
                    <Popover
                      placement="left"
                      content={"This plan group can't be deleted"}
                      trigger="hover"
                    >
                      <div className="text-gray-500">
                        <FolderAddOutlined />
                      </div>
                    </Popover>
                  ))}
              </div>
            </div>
            {getLevels(temp)}
          </Card>
        </Col>
      );
    });
    return output;
  }

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} right={right} />
        <div
          style={{
            height: proHeight - 122,
            overflow: "auto",
          }}
          className="custom-scroll p-6"
        >
          {!loading ? (
            templates.length ? (
              <>
                <div className="mb-3 text-gray-400">
                  You can only add maximum of 10 templates
                </div>
                <Row gutter={16}>
                  {getTemplates()}
                  {templates.length < 10 && (
                    <Col span={8}>
                      {permissionAbility(
                        userLevel,
                        "plan-templates",
                        "add"
                      ) && (
                        <div
                          onClick={() => setAddModal(true)}
                          className="border rounded p-6 text-center text-gray-300 hover:shadow-lg cursor-pointer"
                        >
                          <PlusOutlined />
                        </div>
                      )}
                    </Col>
                  )}
                </Row>
              </>
            ) : (
              <div className="flex justify-center items-center w-full h-full">
                <div className="text-center">
                  <div className="text-3xl text-gray-400">
                    <WarningOutlined />
                  </div>
                  <div className="text-xl pri-font text-gray-400">
                    No templates available
                  </div>
                  {permissionAbility(userLevel, "plan-templates", "add") && (
                    <>
                      <div className="mb-3 text-gray-400">
                        Add plan templates in your organization
                      </div>
                      <Button
                        onClick={() => {
                          setAddModal(true);
                        }}
                        type="primary"
                      >
                        Add template
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
      <AddPlansTemplateModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={getPlanTemplates}
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal("");
          setSelected("");
        }}
        handleOk={() => {
          deleteTemplate();
        }}
        text={`Are you sure want to ${deleteModal} this template?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
    </>
  );
}

export default PlanTemplates;
